import produce from 'immer'

import {
  DeliveryForecastDespatchAdvice,
  DeliveryForecastDespatchAdviceItem,
  WebEdiDeliveryForecastDto,
} from '../../../../data/models'

interface ConvertedResponse
  extends Omit<
    WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>,
    'CreationDate' | 'Payload'
  > {
  CreationDate: Date
  Payload: Payload
}

interface Payload
  extends Omit<
    DeliveryForecastDespatchAdvice,
    | 'OrderDate'
    | 'OrderDeliveryDate'
    | 'ConfirmationDate'
    | 'DeliveryNoteDate'
    | 'DeliveryDate'
  > {
  OrderDate: Date
  OrderDeliveryDate: Date
  ConfirmationDate: Date
  DeliveryNoteDate: Date
  DeliveryDate: Date
  Item: Item
}

interface Item
  extends Omit<
    DeliveryForecastDespatchAdviceItem,
    'DeliveryDate' | 'ConfirmationDeliveryDate'
  > {
  DeliveryDate: Date | null
  ConfirmationDeliveryDate: Date | null
}

/**
 * Converts a list of WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice> responses into a list of converted response objects.
 *
 * This function uses the Immer library to create a new array of converted response objects from
 * the provided WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice> array. The conversion process involves:
 *
 * 1. Transforming date strings into Date objects for easier filtering and sorting in tables.
 * 2. Moving the first item in the 'Items' array to the 'Item' property because a table cannot filter,
 *    sort, or display nested arrays effectively.
 *
 * @param {WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>[] | undefined} responses - The list of responses to be converted.
 * @returns {ConvertedResponse[]} - The list of converted responses.
 */
export function convertDeliveryForecastDespatchAdvice(
  responses:
    | WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>[]
    | undefined,
) {
  // Transforms date strings in date objects so it can be filtered in tables
  const convertedResponses = produce(
    responses,
    (draft: ConvertedResponse[]) => {
      draft?.forEach(response => {
        // CreationDate
        response.CreationDate = new Date(response.CreationDate)
        // OrderDate
        response.Payload.OrderDate = new Date(response.Payload.OrderDate)

        // OrderDeliveryDate
        response.Payload.OrderDeliveryDate = new Date(
          response.Payload.OrderDeliveryDate,
        )

        // ConfirmationDate
        response.Payload.ConfirmationDate = new Date(
          response.Payload.ConfirmationDate,
        )

        // DeliveryNoteDate
        response.Payload.DeliveryNoteDate = new Date(
          response.Payload.DeliveryNoteDate,
        )

        // DeliveryDate
        response.Payload.DeliveryDate = new Date(response.Payload.DeliveryDate)

        // Item
        // The items are moved to item because a table can't filter, sort or
        // display another array inside an array. So we get the first item in
        // the array 'items' and place that in the item as an object.
        if (response.Payload.Items && response.Payload.Items.length > 0) {
          const firstItem = response.Payload.Items[0]

          response.Payload.Item = {
            ...firstItem,
            DeliveryDate: firstItem.DeliveryDate
              ? new Date(firstItem.DeliveryDate)
              : null,
            ConfirmationDeliveryDate: firstItem.ConfirmationDeliveryDate
              ? new Date(firstItem.ConfirmationDeliveryDate)
              : null,
          }
        }
      })
    },
  )

  return convertedResponses
}
